import {
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
    DropdownData,
    InputType,
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { list } from 'src/app/common/classes/request-builder';
import { ClienteModel } from 'src/app/models/cliente.model';
import { ProductosModel } from 'src/app/models/productos/productos.model';
import { TransaccionModel } from 'src/app/models/reto/transaccion.model';
import { fillTipoTransaccion } from '../tipo_transaccion';
import { environment } from 'src/environments/environment';

export class TransaccionesListsDefinition {
    public productos: DropdownData<string> = list<string>();

    public entidadesOrigen: DropdownData<string> = list<string>();

    public ropoOrigen: DropdownData<string | null> = list<string | null>();

    public entidadesDestino: DropdownData<ClienteModel | string> = list<ClienteModel | string>();

    public clientes: DropdownData<ClienteModel> = list<ClienteModel>();

    public tipoTransaccion: DropdownData<string> = fillTipoTransaccion();

    public ropoDestino: DropdownData<string | null> = list<string | null>();
}

export class TransaccionesFormDefinition {
    private readonly formType!: FormType;

    public readonly lists: TransaccionesListsDefinition = new TransaccionesListsDefinition();

    public model: ReactiveFormValue<TransaccionModel> = {} as ReactiveFormValue<TransaccionModel>;

    public lastChangedFieldName: ReactiveFormChangedField<TransaccionModel> = '';

    public formFields!: Form<TransaccionModel>;

    public fieldNames: FormField<TransaccionModel>[] = [];

    public fieldLabels: string[] = [];

    /**
     * Creates the transaction form definition shared by the edit form and list modals.
     *
     * The definition centralizes transaction sections, fields, dropdown lists and
     * conditional visibility so all transaction modes use the same source of truth.
     *
     * @constructor
     * @public
     *
     * @param type - Request type used to define the form behaviour.
     * @param model - Optional transaction model used as initial form data.
     * @param table - Optional table configuration used by table-based form modes.
     * The first item is assigned as table data and the second item as table style.
     *
     * @returns A new transaction form definition instance.
     *
     * @example
     * ```ts
     * const form = new TransaccionesFormDefinition(FormType.CREATION, {});
     * ```
     *
     * @see FormType
     * @see Form
     * @see FormSection
     * @see TransaccionModel
     */
    constructor(
        type: FormType,
        model: Partial<TransaccionModel> | null = {},
        table: (string[] | string[][])[] | null = null,
    ) {
        this.formType = +type;
        this.model = { ...this.model, ...(model ?? {}) } as ReactiveFormValue<TransaccionModel>;
        this.model.tipo_transaccion = this.normalizeTransactionTypeValue(this.model.tipo_transaccion);
        this.formFields = this.buildForm(table);
    }// constructor();

    /**
     * Updates the stored model and rebuilds the form section metadata.
     *
     * @public
     *
     * @param model - Partial transaction model received from the reactive form.
     * @param lastChangedFieldName - Last field changed by the user.
     *
     * @returns Nothing.
     */
    public updateModel(
        model: ReactiveFormValue<TransaccionModel>,
        lastChangedFieldName: ReactiveFormChangedField<TransaccionModel>,
    ): void {
        this.model = { ...this.model, ...model } as ReactiveFormValue<TransaccionModel>;
        this.model.tipo_transaccion = this.normalizeTransactionTypeValue(this.model.tipo_transaccion);
        this.model.importacion_paralela = this.normalizeSwitchValue(this.model.importacion_paralela);
        this.lastChangedFieldName = lastChangedFieldName;
        this.refreshSections();
    }// updateModel();

    /**
     * Rebuilds section visibility and field metadata without replacing dropdown lists.
     *
     * @public
     *
     * @returns Nothing.
     */
    public refreshSections(): void {
        this.fieldNames = [];
        this.fieldLabels = [];
        this.formFields = {
            ...this.formFields,
            sections: this.getFormSections(),
        };
    }// refreshSections();

    /**
     * Builds and returns the transaction main fields.
     *
     * @public
     *
     * @returns The transaction main fields sorted by display order.
     *
     * @remarks
     * These fields cover the responsible entity, responsible ROPO, transaction type,
     * destination entity and destination ROPO. Destination values are resolved from
     * entities or clients depending on the selected transaction type.
     *
     * @see FormField
     * @see TransaccionModel
     * @see InputType
     */
    public getTransactionFields(): FormField<TransaccionModel>[] {
        const transactionFields: FormField<TransaccionModel>[] = [
            {
                name: 'fecha',
                vinculatedFields: undefined,
                label: 'Fecha',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true,
                    calendarConfiguration: { calendarView: 'date', calendarDateFormat: 'dd/mm/yy' },
                },
                width: 4,
                conditions: this.conditions({ isRequired: true }),
                order: 1,
            },
            {
                name: 'id_entidad_origen',
                vinculatedFields: undefined,
                label: 'Entidad responsable',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.entidadesOrigen,
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                width: 4,
                conditions: this.conditions({ isRequired: true }),
                order: 2,
            },
            {
                name: 'ropo_origen',
                vinculatedFields: undefined,
                label: 'ROPO responsable',
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.ropoOrigen,
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                width: 4,
                conditions: this.conditions({ isRequired: true }),
                order: 3,
            },
            {
                name: 'tipo_transaccion',
                vinculatedFields: undefined,
                label: 'Tipo transacción',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.tipoTransaccion,
                    optionLabel: 'label',
                    optionValue: 'value',
                    dropdownConfiguration: { valuePrimaryKey: 'id' },
                },
                width: 4,
                conditions: this.conditions({ isRequired: true }),
                order: 4,
            },
            {
                name: 'id_entidad_destino',
                vinculatedFields: undefined,
                label: 'Entidad destino',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.getDestinationEntityList(),
                    optionLabel: 'label',
                    optionValue: 'value',
                    dropdownConfiguration: this.isApplicationWithClients() ? { valuePrimaryKey: 'id' } : {},
                },
                width: 4,
                conditions: this.conditions(),
                order: 5,
            },
            {
                name: 'ropo_destino',
                vinculatedFields: undefined,
                label: 'ROPO destino',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.ropoDestino,
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                width: 4,
                conditions: this.conditions({ isRequired: this.isDestinationRopoRequired() }),
                order: 6,
            },
        ];

        this.appendVisibleFields(transactionFields);
        return transactionFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getTransactionFields();

    /**
     * Builds and returns the sale fields used only for Spanish sales.
     *
     * @public
     *
     * @returns The sale fields sorted by display order.
     */
    public getSaleFields(): FormField<TransaccionModel>[] {
        const saleFields: FormField<TransaccionModel>[] = [
            {
                name: 'nif_empresa_explotacion',
                vinculatedFields: undefined,
                label: 'NIF del Titular de la Factura',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isRequired: this.isSaleInSpain(), isVisible: this.isSaleInSpain() }),
                order: 1,
            },
        ];

        this.appendVisibleFields(saleFields);
        return saleFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getSaleFields();

    /**
     * Builds and returns the warehouse transfer fields.
     *
     * @public
     *
     * @returns The warehouse fields sorted by display order.
     */
    public getWarehouseFields(): FormField<TransaccionModel>[] {
        const isSectionVisible = this.isWarehouseTransfer();
        const warehouseFields: FormField<TransaccionModel>[] = [
            {
                name: 'descuadre',
                vinculatedFields: undefined,
                label: 'Descuadre',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 4,
                conditions: this.conditions({ isVisible: isSectionVisible }),
                order: 1,
            },
            {
                name: 'almacen_origen',
                vinculatedFields: undefined,
                label: 'Nº almacén responsable',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 4,
                conditions: this.conditions({ isVisible: isSectionVisible }),
                order: 2,
            },
            {
                name: 'almacen_destino',
                vinculatedFields: undefined,
                label: 'Nº almacén de transacción',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 4,
                conditions: this.conditions({ isVisible: isSectionVisible }),
                order: 3,
            },
        ];

        this.appendVisibleFields(warehouseFields);
        return warehouseFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getWarehouseFields();

    /**
     * Builds and returns the product fields.
     *
     * @public
     *
     * @returns The product fields sorted by display order.
     */
    public getProductFields(): FormField<TransaccionModel>[] {
        const productFields: FormField<TransaccionModel>[] = [
            {
                name: 'id_producto',
                vinculatedFields: undefined,
                label: 'Producto',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.productos,
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                width: 4,
                conditions: this.conditions({ isRequired: true }),
                order: 1,
            },
            {
                name: 'num_envases',
                vinculatedFields: undefined,
                label: 'Nº envases',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 4,
                conditions: this.conditions({ isRequired: true }),
                order: 2,
            },
            {
                name: 'capacidad_envase',
                vinculatedFields: undefined,
                label: 'Capacidad de envase',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 4,
                conditions: this.conditions({ isRequired: true }),
                order: 3,
            },
            {
                name: 'volumen_total',
                vinculatedFields: undefined,
                label: 'Volumen total',
                input: { type: InputType.ONLY_TEXT, isEditable: false },
                width: 4,
                conditions: this.conditions(),
                order: 4,
            },
            {
                name: 'lote',
                vinculatedFields: undefined,
                label: 'Lote',
                input: { type: InputType.TEXT, isEditable: true },
                width: 4,
                conditions: this.conditions({ isRequired: true }),
                order: 5,
            },
            {
                name: 'importacion_paralela',
                vinculatedFields: undefined,
                label: 'Importación paralela',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 4,
                conditions: this.conditions(),
                order: 6,
            },
        ];

        this.appendVisibleFields(productFields);
        return productFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getProductFields();

    /**
     * Builds and returns crop treatment fields used by application transactions.
     *
     * @public
     *
     * @returns The crop fields sorted by display order.
     */
    public getCropFields(): FormField<TransaccionModel>[] {
        const isSectionVisible = this.isApplication();
        const cropFields: FormField<TransaccionModel>[] = [
            {
                name: 'cultivo_tratamiento',
                vinculatedFields: undefined,
                label: 'Objeto de tratamiento',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: isSectionVisible }),
                order: 1,
            },
        ];

        this.appendVisibleFields(cropFields);
        return cropFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getCropFields();

    /**
     * Builds and returns NIMA fields used by withdrawal transactions.
     *
     * @public
     *
     * @returns The NIMA fields sorted by display order.
     */
    public getNimaFields(): FormField<TransaccionModel>[] {
        const isSectionVisible = this.isNima();
        const nimaFields: FormField<TransaccionModel>[] = [
            {
                name: 'nima',
                vinculatedFields: undefined,
                label: 'NIMA',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: isSectionVisible }),
                order: 1,
            },
        ];

        this.appendVisibleFields(nimaFields);
        return nimaFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getNimaFields();

    /**
     * Builds and returns hidden transaction fields required by submit actions.
     *
     * The delete modal also submits through the reactive form action button, so the
     * primary key must exist as a hidden control to avoid DELETE requests without id.
     *
     * @public
     *
     * @returns The hidden transaction fields sorted by display order.
     */
    public getHiddenFields(): FormField<TransaccionModel>[] {
        const hiddenFields: FormField<TransaccionModel>[] = [
            {
                name: 'id',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id,
                    isEditable: false,
                },
                width: 3,
                conditions: this.conditions(),
                order: 1,
            },
        ];

        if (!this.isSaleInSpain()) {
            hiddenFields.push({
                name: 'nif_empresa_explotacion',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.nif_empresa_explotacion,
                    isEditable: false,
                },
                width: 3,
                conditions: this.conditions(),
                order: 2,
            });
        }// if();

        return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getHiddenFields();

    /**
     * Updates the selected product units in the transaction model.
     *
     * @public
     *
     * @param products - Product records used to resolve the selected product units.
     *
     * @returns Nothing.
     */
    public syncSelectedProductUnits(products: ProductosModel[]): void {
        const selectedProductId = String(this.model.id_producto ?? '');
        const product = products.find((currentProduct) => String(currentProduct.id ?? '') === selectedProductId);

        this.model.unidades = product?.unidades?.toString() ?? '';
    }// syncSelectedProductUnits();

    /**
     * Recalculates the total volume from container count and capacity.
     *
     * @public
     *
     * @returns Nothing.
     */
    public calculateTotalVolume(): void {
        if (this.model.num_envases == null || this.model.capacidad_envase == null) {
            this.model.volumen_total = '';
            return;
        }// if();

        const packageCount = Number.parseFloat(String(this.model.num_envases).replace(',', '.'));
        const packageCapacity = Number.parseFloat(String(this.model.capacidad_envase).replace(',', '.'));

        if (Number.isNaN(packageCount) || Number.isNaN(packageCapacity)) {
            this.model.volumen_total = '';
            return;
        }// if();

        this.model.volumen_total = (packageCount * packageCapacity).toFixed(2);
    }// calculateTotalVolume();

    /**
     * Clears warehouse fields when the current transaction is not a warehouse transfer.
     *
     * @public
     *
     * @returns Nothing.
     */
    public deleteWarehouseData(): void {
        delete this.model.almacen_origen;
        delete this.model.almacen_destino;
    }// deleteWarehouseData();

    /**
     * Indicates whether the current transaction requires warehouse fields.
     *
     * @public
     *
     * @returns True when the origin and destination entity and ROPO are the same for warehouse types.
     */
    public isWarehouseTransfer(): boolean {
        const transactionType = this.getTransactionType();

        if (transactionType !== '11' && transactionType !== '12') {
            return false;
        }// if();

        return Boolean(
            this.model.id_entidad_origen != null &&
            this.model.id_entidad_destino != null &&
            this.model.ropo_origen != null &&
            this.model.ropo_destino != null &&
            String(this.model.id_entidad_origen) === String(this.model.id_entidad_destino) &&
            String(this.model.ropo_origen) === String(this.model.ropo_destino),
        );
    }// isWarehouseTransfer();

    /**
     * Indicates whether the current transaction must show the NIMA section.
     *
     * @public
     *
     * @returns True when the transaction type is a NIMA-related withdrawal.
     */
    public isNima(): boolean {
        const transactionType = this.getTransactionType();

        return transactionType === '9' || transactionType === '10';
    }// isNima();

    /**
     * Builds and returns the transaction form sections.
     *
     * @private
     *
     * @returns The transaction form sections sorted by display order.
     */
    private getFormSections(): FormSection<TransaccionModel>[] {
        const sections: FormSection<TransaccionModel>[] = [
            {
                name: 'Transacción',
                fields: this.getTransactionFields(),
                width: 12,
                isVisible: true,
                order: 1,
            },
            {
                name: 'Venta',
                fields: this.getSaleFields(),
                width: 12,
                isVisible: this.isSaleInSpain(),
                order: 2,
            },
            {
                name: 'Almacén',
                fields: this.getWarehouseFields(),
                width: 12,
                isVisible: this.isWarehouseTransfer(),
                order: 3,
            },
            {
                name: 'Producto',
                fields: this.getProductFields(),
                width: 12,
                isVisible: true,
                order: 4,
            },
            {
                name: 'Cultivo',
                fields: this.getCropFields(),
                width: 12,
                isVisible: this.isApplication(),
                order: 5,
            },
            {
                name: 'NIMA',
                fields: this.getNimaFields(),
                width: 12,
                isVisible: this.isNima(),
                order: 6,
            },
            {
                name: '',
                fields: this.getHiddenFields(),
                width: 12,
                isVisible: true,
                order: 7,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getFormSections();

    /**
     * Builds the root form metadata object.
     *
     * @private
     *
     * @param table - Optional table configuration used by table-based form modes.
     *
     * @returns The root form metadata object.
     */
    private buildForm(table: (string[] | string[][])[] | null): Form<TransaccionModel> {
        return {
            name: 'transacciones',
            header: this.getHeader(),
            type: this.formType,
            sections: this.getFormSections(),
            table: table === null
                ? null
                : { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
            serializer: {
                fields: {
                    importacion_paralela: { mode: 'boolean', trueValue: 1, falseValue: 0 },
                    descuadre: { mode: 'boolean', trueValue: 1, falseValue: 0 },
                },
            },
        };
    }// buildForm();

    /**
     * Normalizes payload values that are sensitive to display formatting.
     *
     * @private
     *
     * @param payload - Raw payload generated by the reactive form.
     *
     * @returns The normalized payload ready for the API request.
     */
    /**
     * Converts backend switch values to the numeric values expected by PrimeNG.
     *
     * @private
     *
     * @param value - Raw switch value received from the form or backend.
     *
     * @returns `1` when enabled, otherwise `0`.
     */
    private normalizeSwitchValue(value: unknown): 1 | 0 {
        if (value === 1 || value === true) {
            return 1;
        }// if();

        if (typeof value === 'string') {
            const normalizedValue = value.trim().toLowerCase();

            return ['1', 'true', 'si', 'sí', 's', 'yes'].includes(normalizedValue) ? 1 : 0;
        }// if();

        return 0;
    }// normalizeSwitchValue();

    /**
     * Returns the form header based on the active form mode.
     *
     * @private
     *
     * @returns Human-readable form header.
     */
    private getHeader(): string {
        switch (this.formType) {
            case FormType.CREATION:
                return 'Crear transacción';
            case FormType.DUPLICATION:
                return 'Duplicar transacción';
            case FormType.EDITION:
                return 'Editar transacción';
            default:
                return 'Datos de la transacción';
        }
    }// getHeader();

    /**
     * Creates field conditions using the project defaults.
     *
     * @private
     *
     * @param overrides - Partial condition values to override the defaults.
     *
     * @returns A complete field conditions object.
     */
    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }// conditions();

    /**
     * Appends visible fields to table/export metadata arrays.
     *
     * @private
     *
     * @param fields - Fields to inspect and append when visible.
     *
     * @returns Nothing.
     */
    private appendVisibleFields(fields: FormField<TransaccionModel>[]): void {
        const visibleFields = fields.filter((field) => field.conditions.isVisible);

        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((field) => field.label));
    }// appendVisibleFields();

    /**
     * Converts transaction type labels or numeric backend values to the option code used by the dropdown.
     *
     * @private
     *
     * @param value - Raw transaction type value received from the backend or form.
     *
     * @returns Transaction type code as a string.
     */
    private normalizeTransactionTypeValue(value: unknown): string {
        const normalizedValue = String(value ?? '').trim();
        const codeMatch = /^(\d+)/.exec(normalizedValue);

        return codeMatch?.[1] ?? normalizedValue;
    }// normalizeTransactionTypeValue();

    /**
     * Returns the normalized transaction type value.
     *
     * @private
     *
     * @returns Transaction type as a string code.
     */
    private getTransactionType(): string {
        return this.normalizeTransactionTypeValue(this.model.tipo_transaccion);
    }// getTransactionType();

    /**
     * Indicates whether the transaction is a Spanish sale.
     *
     * @private
     *
     * @returns True when the transaction type is `2`.
     */
    private isSaleInSpain(): boolean {
        return this.getTransactionType() === '2';
    }// isSaleInSpain();

    /**
     * Indicates whether the transaction is an application.
     *
     * @private
     *
     * @returns True when the transaction type is `8`.
     */
    private isApplication(): boolean {
        return this.getTransactionType() === '8';
    }// isApplication();

    /**
     * Indicates whether application transactions should use clients as destination.
     *
     * @private
     *
     * @returns True when clients are enabled and the transaction type is `8`.
     */
    private isApplicationWithClients(): boolean {
        return this.isApplication() && Boolean(environment.features.hasClients);
    }// isApplicationWithClients();

    /**
     * Returns the dropdown list used by the destination entity field.
     *
     * @private
     *
     * @returns Client list for application transactions, otherwise entity list.
     */
    private getDestinationEntityList(): DropdownData<ClienteModel | string> | DropdownData<ClienteModel> {
        return this.isApplicationWithClients() ? this.lists.clientes : this.lists.entidadesDestino;
    }// getDestinationEntityList();

    /**
     * Indicates whether destination ROPO is required.
     *
     * @private
     *
     * @returns False for empty transaction type and application transactions, otherwise true.
     */
    private isDestinationRopoRequired(): boolean {
        const transactionType = this.getTransactionType();

        return transactionType !== '' && transactionType !== '0' && transactionType !== '8';
    }// isDestinationRopoRequired();
}// class;
